import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { Download } from "lucide-react";

/* ── Helpers ─────────────────────────────────────────────────────────────────── */
const JOURS_FULL = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
const MOIS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août",
  "septembre", "octobre", "novembre", "décembre"];

function formatFullDate(dateStr) {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return `${JOURS_FULL[d.getDay()]} ${d.getDate()} ${MOIS_FR[d.getMonth()]}`;
  } catch { return dateStr; }
}

function formatDuration(minutes) {
  if (!minutes) return null;
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h${m.toString().padStart(2, "0")}` : `${h}h`;
}

/* ── Printable Document ──────────────────────────────────────────────────────── */
const PrintableRoadmap = React.forwardRef(({ roadmap, trip }, ref) => {
  const jours = roadmap?.jours || [];

  const s = {
    page:       { fontFamily: "Georgia, 'Times New Roman', serif", background: "#F9F6F0", color: "#2B2623", padding: "32px 40px", maxWidth: "740px", margin: "0 auto" },
    cover:      { borderBottom: "2px solid #C17F59", paddingBottom: "20px", marginBottom: "28px" },
    logo:       { fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "1rem", color: "#C17F59", marginBottom: "8px", letterSpacing: "0.02em" },
    mainTitle:  { fontFamily: "Georgia, serif", fontSize: "2.2rem", fontWeight: 600, color: "#2B2623", margin: "0 0 2px 0", lineHeight: 1.1 },
    subtitle:   { fontFamily: "Georgia, serif", fontSize: "0.9rem", color: "#9A8F86", fontStyle: "italic", margin: "0 0 6px 0" },
    label:      { fontFamily: "Georgia, serif", fontSize: "0.68rem", color: "#C17F59", textTransform: "uppercase", letterSpacing: "0.1em" },

    dayWrap:    { marginBottom: "28px", pageBreakInside: "avoid" },
    dayHead:    { borderLeft: "3px solid #C17F59", paddingLeft: "12px", marginBottom: "10px" },
    dayNum:     { fontFamily: "Georgia, serif", fontSize: "1.35rem", fontWeight: 600, color: "#2B2623", margin: 0, lineHeight: 1.2 },
    dayDate:    { fontFamily: "Georgia, serif", fontSize: "0.78rem", color: "#9A8F86", margin: "1px 0 0 0" },
    dayTitre:   { fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "1rem", color: "#8B5E34", margin: "2px 0 0 0" },
    reveil:     { fontFamily: "Georgia, serif", fontSize: "0.75rem", color: "#C17F59", margin: "4px 0 0 0" },
    conseil:    { fontFamily: "Georgia, serif", fontSize: "0.78rem", color: "#5C554E", fontStyle: "italic", background: "#FDF8F3", borderLeft: "2px solid #DCD4C6", padding: "6px 10px", margin: "8px 0 10px 0", lineHeight: 1.5 },

    periodWrap: { marginBottom: "8px" },
    periodLabel:{ fontFamily: "Georgia, serif", fontSize: "0.72rem", color: "#8B5E34", textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "1px solid #EDE8E0", paddingBottom: "3px", marginBottom: "5px" },

    actRow:     { display: "flex", alignItems: "flex-start", gap: "8px", padding: "4px 0", borderBottom: "1px dotted #EDE8E0" },
    actIcon:    { fontSize: "0.95rem", flexShrink: 0, width: "22px", textAlign: "center", marginTop: "1px" },
    actBody:    { flex: 1, minWidth: 0 },
    actLine1:   { display: "flex", alignItems: "baseline", gap: "6px", flexWrap: "wrap" },
    actTime:    { fontFamily: "Georgia, serif", fontSize: "0.82rem", fontWeight: 600, color: "#C17F59", flexShrink: 0 },
    actTitle:   { fontFamily: "Georgia, serif", fontSize: "0.85rem", color: "#2B2623" },
    actMeta:    { fontFamily: "Georgia, serif", fontSize: "0.72rem", color: "#9A8F86", marginTop: "1px" },
    actNote:    { fontFamily: "Georgia, serif", fontSize: "0.72rem", color: "#9A8F86", fontStyle: "italic", marginTop: "1px" },
    freeTag:    { display: "inline", fontSize: "0.7rem", color: "#166534", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "0px 5px", marginLeft: "4px" },

    footer:     { marginTop: "40px", borderTop: "1px solid #DCD4C6", paddingTop: "12px", textAlign: "center" },
    footerTxt:  { fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "0.75rem", color: "#9A8F86" },
    separator:  { border: "none", borderTop: "1px solid #DCD4C6", margin: "20px 0" },
  };

  const PERIOD_LABELS = {
    matin:      "Matin",
    apres_midi: "Après-midi",
    soiree:     "Soirée",
  };
  const PERIOD_EMOJI = { matin: "🌅", apres_midi: "🌞", soiree: "🌙" };

  const renderActivities = (activites = []) =>
    activites.map((a, i) => {
      const dur = formatDuration(a.duree_minutes);
      const showPrice = a.prix_euros !== -1 && a.prix_euros !== undefined && a.prix_euros !== null;
      const isFree = a.prix_euros === 0;
      const metaParts = [a.lieu, dur ? `~${dur}` : null].filter(Boolean);
      return (
        <div key={a.id || i} style={s.actRow}>
          <span style={s.actIcon}>{a.icone}</span>
          <div style={s.actBody}>
            <div style={s.actLine1}>
              <span style={s.actTime}>{a.heure_debut}</span>
              <span style={s.actTitle}>{a.titre}</span>
              {showPrice && isFree && <span style={s.freeTag}>Gratuit</span>}
              {showPrice && !isFree && (
                <span style={{ ...s.actMeta, marginLeft: "2px" }}>~{a.prix_euros}€</span>
              )}
            </div>
            {metaParts.length > 0 && (
              <p style={s.actMeta}>📍 {metaParts.join(" · ")}</p>
            )}
            {a.note && <p style={s.actNote}>— {a.note}</p>}
          </div>
        </div>
      );
    });

  const renderPeriod = (key, data) => {
    const activites = data?.activites || [];
    if (activites.length === 0) return null;
    return (
      <div key={key} style={s.periodWrap}>
        <p style={s.periodLabel}>{PERIOD_EMOJI[key]} {PERIOD_LABELS[key]}</p>
        {renderActivities(activites)}
      </div>
    );
  };

  // Date range for cover
  const dateRange = [trip?.date_depart, trip?.date_retour]
    .filter(Boolean)
    .map((d) => new Date(d + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }))
    .join(" — ");

  const nbJours = jours.length;

  return (
    <div ref={ref} style={s.page}>
      {/* Cover */}
      <div style={s.cover}>
        <p style={s.logo}>Voyage-r</p>
        <h1 style={s.mainTitle}>{trip?.destination}</h1>
        <p style={s.subtitle}>
          Feuille de Route · {nbJours} jour{nbJours > 1 ? "s" : ""}
          {dateRange ? ` · ${dateRange}` : ""}
          {trip?.nb_adultes ? ` · ${trip.nb_adultes} voyageur${trip.nb_adultes > 1 ? "s" : ""}` : ""}
        </p>
      </div>

      {/* Days */}
      {jours.map((jour, idx) => (
        <div key={jour.numero_jour} style={s.dayWrap}>
          {/* Day header */}
          <div style={s.dayHead}>
            <p style={s.dayNum}>Jour {jour.numero_jour}</p>
            <p style={s.dayDate}>{formatFullDate(jour.date)}</p>
            {jour.titre_jour && <p style={s.dayTitre}>{jour.titre_jour}</p>}
            {jour.heure_reveil && <p style={s.reveil}>Réveil : {jour.heure_reveil}</p>}
          </div>

          {/* Conseil du jour */}
          {jour.conseil_jour && (
            <p style={s.conseil}>💡 {jour.conseil_jour}</p>
          )}

          {/* Periods */}
          {renderPeriod("matin",      jour.matin)}
          {renderPeriod("apres_midi", jour.apres_midi)}
          {renderPeriod("soiree",     jour.soiree)}

          {idx < jours.length - 1 && <hr style={s.separator} />}
        </div>
      ))}

      {/* Footer */}
      <div style={s.footer}>
        <p style={s.footerTxt}>
          Voyage-r · Feuille de route générée par votre conseiller voyage · © {new Date().getFullYear()}
        </p>
        <p style={{ ...s.footerTxt, marginTop: "3px" }}>
          Document créé le {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
        </p>
      </div>
    </div>
  );
});
PrintableRoadmap.displayName = "PrintableRoadmap";

/* ── Export Button ───────────────────────────────────────────────────────────── */
export function RoadmapPDFButton({ roadmap, trip }) {
  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Voyage-r — Feuille de Route — ${trip?.destination || ""}`,
    pageStyle: `
      @page { size: A4; margin: 10mm 12mm; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .no-print { display: none !important; }
      }
    `,
  });

  return (
    <>
      <button
        onClick={handlePrint}
        className="inline-flex items-center gap-2 px-4 py-2 border border-[#DCD4C6] text-[#5C554E] hover:border-[#C17F59] hover:text-[#C17F59] transition-colors text-sm"
        style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "1px" }}
        data-testid="roadmap-pdf-btn"
      >
        <Download size={13} strokeWidth={1.5} />
        Exporter en PDF
      </button>

      {/* Hidden printable document */}
      <div style={{ display: "none" }}>
        <PrintableRoadmap ref={printRef} roadmap={roadmap} trip={trip} />
      </div>
    </>
  );
}
